import {
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  type Scene,
  SphereGeometry,
  type Vector3,
} from 'three';
import type { AbilityDef } from '../characters/abilities';
import type { LevelCollider } from '../collision/LevelCollider';
import type { AbilityWorld } from '../game/AbilityWorld';
import { PLAYER_HEIGHT, PLAYER_RADIUS } from '../movement/constants';
import { PlayerController } from '../movement/PlayerController';
import type { MovementTuning } from '../movement/tuning';
import { Duelist } from './ai/Duelist';
import type { CombatWorld } from './CombatWorld';
import { buildHealthBar, disposeGroup, drawHealthBar, type HealthBarSprite } from './TargetDummies';

/** An AI champion a map declares (spec 0028). */
export interface DuelistSpec {
  id: string;
  /** Roster hero whose kit/feel it plays (visual accent + abilities). */
  heroId: string;
  /** Spawn feet position (also the respawn point). */
  position: Vector3;
  maxHealth: number;
  respawnSec: number;
}

/**
 * Presenter + host for one AI champion (spec 0028): owns its controller
 * (per-instance tuning, spec 0027), its Duelist brain, its mesh and health
 * bar. Registers into the CombatWorld; on respawn it teleports home.
 */
export class DuelistChampion {
  private readonly group = new Group();
  private readonly bar: HealthBarSprite;
  private lastBarHealth: number;
  private wasAlive = true;
  private readonly controller: PlayerController;
  private readonly brain: Duelist;

  constructor(
    private readonly scene: Scene,
    private readonly combat: CombatWorld,
    private readonly spec: DuelistSpec,
    opts: {
      collider: LevelCollider;
      killY: number;
      tuning: MovementTuning;
      abilities: readonly AbilityDef[];
      castRange: Record<string, number>;
      accent: number;
      world: AbilityWorld;
      targetPos: () => Vector3;
    },
  ) {
    this.controller = new PlayerController(
      opts.collider,
      spec.position.clone(),
      opts.killY,
      opts.tuning,
    );
    this.brain = new Duelist({
      id: spec.id,
      controller: this.controller,
      combat,
      abilities: opts.abilities,
      castRange: opts.castRange,
      world: opts.world,
      targetPos: opts.targetPos,
    });

    const body = new MeshStandardMaterial({
      color: opts.accent,
      emissive: opts.accent,
      emissiveIntensity: 0.35,
      roughness: 0.5,
    });
    const torso = new Mesh(
      new CylinderGeometry(PLAYER_RADIUS, PLAYER_RADIUS, PLAYER_HEIGHT - PLAYER_RADIUS, 12),
      body,
    );
    torso.position.y = (PLAYER_HEIGHT - PLAYER_RADIUS) / 2;
    const head = new Mesh(new SphereGeometry(PLAYER_RADIUS * 0.85, 12, 10), body);
    head.position.y = PLAYER_HEIGHT;
    this.bar = buildHealthBar(PLAYER_HEIGHT + 40);
    this.group.add(torso, head, this.bar.sprite);
    this.group.position.copy(spec.position);
    this.lastBarHealth = spec.maxHealth;

    scene.add(this.group);
    combat.register({
      id: spec.id,
      team: 'red',
      maxHealth: spec.maxHealth,
      radius: PLAYER_RADIUS,
      height: PLAYER_HEIGHT,
      getPosition: () => this.controller.position,
      respawnSec: spec.respawnSec,
    });
  }

  /** Fixed-tick step: think/move while alive; teleport home on revive. */
  tick(dt: number): void {
    const state = this.combat.state(this.spec.id);
    const alive = state?.alive ?? true;
    this.group.visible = alive;
    if (alive) {
      if (!this.wasAlive) {
        this.controller.teleport(this.spec.position.clone()); // fresh spawn, zeroed speed
      }
      this.brain.tick(dt);
      this.group.position.copy(this.controller.position);
    }
    this.wasAlive = alive;
  }

  /** Render-side: redraw the health bar when it changed. */
  updateBar(): void {
    const state = this.combat.state(this.spec.id);
    if (!state || state.health === this.lastBarHealth) return;
    this.lastBarHealth = state.health;
    drawHealthBar(this.bar.canvas, state.health / this.spec.maxHealth);
    this.bar.texture.needsUpdate = true;
  }

  clear(): void {
    this.scene.remove(this.group);
    disposeGroup(this.group);
    this.bar.texture.dispose();
  }
}
